import React from 'react';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { useAdminSearch } from '../../shared/useAdminSearch';
import { useFeatureFlagSearchOptions } from '../../shared/useFeatureFlagSearchOptions';
import { AdminDataGrid } from '../../shared/AdminDataGrid';
import { useListAllFeatureFlagsQuery, useUpdateFeatureFlagMutation } from '../api/featureFlagsApi';
import { featureFlagColumns } from './columns';
import { useFeatureFlagFilter } from './useFeatureFlagFilter';

/**
 * The admin feature flags grid. Loads every flag, then filters (filters, from the page's
 * toolbar: the users, broker groups and ledgers they directly target), searches (by id,
 * feature, description, ledger, and the names / emails of its users and broker groups),
 * sorts and pages them in the browser. With a search and no column sort, rows are in
 * relevance order. Descriptions are edited in their cell and saved when the edit ends.
 *
 * @param {object} props
 * @param {string} props.search from useAdminListParams
 * @param {object} props.filters from useAdminListParams
 * @param {object} props.gridProps from useAdminListParams
 */
export const FeatureFlagsTable = ({ search, filters, gridProps }) => {
	const dispatch = useDispatch();
	const { data: flags = [], isLoading, isError } = useListAllFeatureFlagsQuery();
	const filteredFlags = useFeatureFlagFilter(flags, filters);
	const rows = useAdminSearch(filteredFlags, useFeatureFlagSearchOptions(), search);
	const [updateFeatureFlag] = useUpdateFeatureFlagMutation();

	// Saves an edited description (the column refuses empty text). Throwing keeps the old row.
	const handleRowUpdate = async (newRow, oldRow) => {
		const description = newRow.description.trim();
		if (description === oldRow.description) {
			return oldRow;
		}
		await updateFeatureFlag({ id: newRow.id, description }).unwrap();
		return { ...newRow, description };
	};

	const handleRowUpdateError = () =>
		dispatch(
			showToast({
				message: 'Could not save the description. Please try again.',
				error: true,
			}),
		);

	return (
		<AdminDataGrid
			table='featureFlags'
			columns={featureFlagColumns}
			rows={rows}
			isLoading={isLoading}
			isError={isError}
			objectType='feature flags'
			processRowUpdate={handleRowUpdate}
			onProcessRowUpdateError={handleRowUpdateError}
			{...gridProps}
		/>
	);
};
